import { useEffect, useRef, useState } from 'react';
import type { GameState, Move, Player } from '@tentactoe/engine';
import type { BotRequest, BotResponse } from './protocol';

export interface BotOpponentOptions {
  readonly enabled: boolean;
  readonly botId: string;
  readonly side: Player;
  readonly effort?: number;
  /** Floor on how fast a reply is shown, so instant bots do not feel jarring. */
  readonly minThinkMs?: number;
}

export interface BotOpponent {
  readonly thinking: boolean;
  readonly error: string | null;
}

const DEFAULT_MIN_THINK_MS = 350;

/**
 * Plays one side of the game with a bot, off the main thread.
 *
 * Replies are matched to requests by id and discarded if they arrive after the
 * position has moved on — which happens routinely when the player hits undo or
 * starts a new game while the bot is still searching.
 */
export function useBotOpponent(
  state: GameState,
  play: (move: Move) => void,
  options: BotOpponentOptions,
): BotOpponent {
  const { enabled, botId, side, effort, minThinkMs = DEFAULT_MIN_THINK_MS } = options;

  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  // `play` changes identity whenever state does; a ref keeps the worker's
  // message handler from having to be torn down and rebuilt on every move.
  const playRef = useRef(play);
  playRef.current = play;

  useEffect(() => {
    const worker = new Worker(new URL('./bot.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<BotResponse>) => {
      const response = event.data;
      // Ignore anything but the reply to the most recent request.
      if (response.id !== requestIdRef.current) return;

      setThinking(false);
      if (response.ok) {
        setError(null);
        playRef.current(response.move);
      } else {
        setError(response.error);
      }
    };

    worker.onerror = (event) => {
      setThinking(false);
      setError(event.message || 'bot worker failed');
    };

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const isBotTurn = enabled && state.result === null && state.turn === side;

  useEffect(() => {
    if (!isBotTurn) {
      setThinking(false);
      return;
    }

    const worker = workerRef.current;
    if (!worker) return;

    const id = ++requestIdRef.current;
    setThinking(true);

    // Seed from the position so the same game replays identically.
    const seed = state.moves.length * 2654435761 + state.moves.reduce((a, m) => a * 31 + m, 7);

    const request: BotRequest = {
      id,
      botId,
      moves: [...state.moves],
      seed: seed >>> 0,
      ...(effort !== undefined ? { effort } : {}),
    };

    const timer = setTimeout(() => worker.postMessage(request), minThinkMs);
    return () => clearTimeout(timer);
  }, [isBotTurn, botId, side, effort, minThinkMs, state.moves]);

  return { thinking, error };
}
